import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import TrueWellnessHeader from "@/components/truewellness/TrueWellnessHeader";
import TrueWellnessFooter from "@/components/truewellness/TrueWellnessFooter";
import LearnMoreModal from "@/components/truewellness/LearnMoreModal";
import NudaVitaeAssessmentModal from "@/components/truewellness/NudaVitaeAssessmentModal";
import { wellnessBlogContent, wellnessBlogPosts } from "@/data/wellnessBlogPosts";
import ReactMarkdown from "react-markdown";

// Import images
import glp1Background from "@/assets/glp1-weight-loss.png";
import weightLossJourneyHero from "@/assets/weight-loss-journey-hero.png";
import trueenergyBackground from "@/assets/trueenergy-background.png";
import truemoodBackground from "@/assets/truemood-background.png";
import truesleepBackground from "@/assets/truesleep-background.png";
import truehairBackground from "@/assets/truehair-background.png";
import trueskinBackground from "@/assets/trueskin-background.png";
import truerecoveryBackground from "@/assets/truerecovery-background.png";
import employerBenefitsBackground from "@/assets/employer-benefits-background.png";
import heroImage from "@/assets/hero-image.png";
import springBreakStack from "@/assets/spring-break-stack.png";
import summerMaintenanceStack from "@/assets/summer-maintenance-stack.png";
import fluDefenseStack from "@/assets/flu-defense-stack.png";
import gutRestorationStack from "@/assets/gut-restoration-stack.png";
import surgeryStack from "@/assets/surgery-stack.jpg";
import athleticStack from "@/assets/athletic-stack.jpg";
import stressBurnoutStack from "@/assets/stress-burnout-stack.png";
import longevityStack from "@/assets/longevity-stack.jpg";
import metabolicResetStack from "@/assets/metabolic-reset-stack.jpg";
import executivePerformanceStack from "@/assets/executive-performance-stack.png";
import radianceStack from "@/assets/radiance-skin-stack.png";
import harmonyStack from "@/assets/harmony-stack.png";

const imageMap: Record<string, string> = {
  "glp1-weight-loss": weightLossJourneyHero,
  "energy": trueenergyBackground,
  "mood": truemoodBackground,
  "sleep": truesleepBackground,
  "hair": truehairBackground,
  "skin": trueskinBackground,
  "recovery": truerecoveryBackground,
  "employer-benefits": employerBenefitsBackground,
  "hero": heroImage,
  "spring-break": springBreakStack,
  "summer": summerMaintenanceStack,
  "flu-defense": fluDefenseStack,
  "gut-restoration": gutRestorationStack,
  "surgery": surgeryStack,
  "athletic": athleticStack,
  "stress-burnout": stressBurnoutStack,
  "longevity": longevityStack,
  "metabolic": metabolicResetStack,
  "executive-performance": executivePerformanceStack,
  "radiance": radianceStack,
  "harmony": harmonyStack,
};

const WellnessBlogPost = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);

  const handleOpenModal = () => {
    setLearnMoreOpen(true);
  };

  const post = postId ? wellnessBlogContent[postId] : null;
  const postMeta = wellnessBlogPosts.find(p => p.id === postId);

  if (!post || !postMeta) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Post not found</h1>
          <Button onClick={() => navigate("/wellness/blog")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  const heroImage = imageMap[post.image] || glp1Background;

  return (
    <>
      <Helmet>
        <title>{post.title} | TrueWellness Blog</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.keywords} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`https://www.mytruewellness.com/wellness/blog/${postId}`} />
        
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.metaDescription,
            "datePublished": post.date,
            "dateModified": post.date,
            "author": {
              "@type": "Organization",
              "name": "TrueWellness",
              "url": "https://www.mytruewellness.com"
            },
            "publisher": {
              "@type": "Organization",
              "name": "TrueWellness",
              "url": "https://www.mytruewellness.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.mytruewellness.com/favicon.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://www.mytruewellness.com/wellness/blog/${postId}`
            },
            "articleSection": postMeta.category,
            "keywords": post.keywords
          })}
        </script>
        
        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.mytruewellness.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Wellness Blog",
                "item": "https://www.mytruewellness.com/wellness/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": post.title,
                "item": `https://www.mytruewellness.com/wellness/blog/${postId}`
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <TrueWellnessHeader onOpenModal={handleOpenModal} />

        <main className="pt-20">
          {/* Hero Image */}
          <div 
            className="w-full h-72 md:h-[28rem] bg-cover bg-center relative"
            style={{ backgroundImage: `url(${heroImage})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
              <div className="container mx-auto max-w-3xl">
                <span className="inline-block px-4 py-1.5 bg-wellness text-white text-xs font-bold uppercase tracking-wider rounded-full mb-4">
                  {postMeta.category}
                </span>
                <p className="text-muted-foreground/80 text-sm font-medium">
                  {new Date(post.date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="container mx-auto px-4 py-12 md:py-16 max-w-3xl">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/wellness/blog")}
              className="mb-10 text-muted-foreground hover:text-foreground -ml-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>

            <article className="prose prose-lg max-w-none 
              prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-foreground
              prose-h1:text-3xl prose-h1:md:text-4xl prose-h1:leading-tight prose-h1:mb-6
              prose-h2:text-2xl prose-h2:md:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:border-b prose-h2:border-border/50 prose-h2:pb-3
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-h4:text-lg prose-h4:font-semibold prose-h4:mt-6 prose-h4:mb-2
              prose-p:text-base prose-p:leading-relaxed prose-p:text-foreground/85 prose-p:mb-4
              prose-strong:text-foreground prose-strong:font-semibold
              prose-li:text-base prose-li:text-foreground/85 prose-li:leading-relaxed prose-li:my-1
              prose-ul:my-4 prose-ul:pl-0 prose-ol:my-4
              prose-hr:my-10 prose-hr:border-border/40
              prose-blockquote:border-l-4 prose-blockquote:border-wellness prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-foreground/70 prose-blockquote:my-6
              prose-a:text-wellness prose-a:font-medium prose-a:no-underline hover:prose-a:underline
              prose-img:cursor-pointer prose-img:transition-transform prose-img:hover:scale-[1.02] prose-img:rounded-xl prose-img:shadow-lg
            ">
              <ReactMarkdown
                components={{
                  img: ({ node, ...props }) => (
                    <img 
                      {...props} 
                      onClick={handleOpenModal}
                      className="cursor-pointer transition-all hover:scale-[1.02] hover:shadow-xl rounded-xl shadow-lg my-6"
                      title="Click to book a consultation"
                    />
                  )
                }}
              >
                {post.content}
              </ReactMarkdown>
            </article>

            {/* Disclosure Statement */}
            <div className="mt-16 p-8 bg-muted/30 rounded-2xl border border-border/50">
              <h3 className="text-xl font-bold text-foreground mb-4">Telehealth & Compounded Peptide Disclosure</h3>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                By participating in peptide-based wellness services, you acknowledge and agree to the following:
              </p>
              
              <div className="grid gap-5 text-sm text-muted-foreground">
                <div>
                  <h4 className="font-semibold text-foreground mb-1.5">Telehealth Evaluation & Provider Oversight</h4>
                  <p className="leading-relaxed">Services are delivered through a telehealth model. A licensed medical provider will review your health information and, in their independent medical judgment, determine whether peptide therapy is appropriate for you. Participation is voluntary and not guaranteed.</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-foreground mb-1.5">Compounded Medications</h4>
                  <p className="leading-relaxed">Peptides, when prescribed, may be compounded medications prepared by state-licensed compounding pharmacies. Compounded drugs are not FDA-approved, and the FDA does not evaluate their safety, effectiveness, or quality prior to dispensing. These medications are prescribed only when a provider determines a patient-specific clinical need exists.</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-foreground mb-1.5">Off-Label & Investigational Use</h4>
                  <p className="leading-relaxed">Peptide therapies are commonly prescribed for off-label or investigational purposes and are not FDA-approved for many wellness, optimization, or longevity indications. Individual results may vary.</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-foreground mb-1.5">Risks & Patient Responsibility</h4>
                  <p className="leading-relaxed">While many individuals experience only mild side effects (such as injection site reactions, flushing, or dizziness), more serious adverse events may occur in rare cases. You agree to seek prompt medical attention for any concerning symptoms and to communicate health changes to your provider.</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-foreground mb-1.5">Self-Pay Services</h4>
                  <p className="leading-relaxed">Peptide therapy and related services are not covered by insurance. All services are offered on a self-pay basis, and payment is required prior to fulfillment.</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-foreground mb-1.5">Not Emergency or Primary Care</h4>
                  <p className="leading-relaxed">These services do not replace primary care and are not intended for emergency situations. In the event of a medical emergency, call 911 or seek immediate in-person medical care.</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 p-10 bg-gradient-to-br from-wellness/10 via-wellness/5 to-transparent rounded-2xl border border-wellness/20 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Ready to Start Your Personalized Wellness Journey?
              </h3>
              <p className="text-muted-foreground mb-8 text-lg max-w-xl mx-auto">
                Get access to personalized peptide protocols through our network of licensed providers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="font-bold bg-wellness hover:bg-wellness/90 px-8 py-6 text-base"
                  onClick={handleOpenModal}
                >
                  Take 3-min Assessment
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="font-bold border-wellness text-wellness hover:bg-wellness/10 px-8 py-6 text-base"
                  onClick={handleOpenModal}
                >
                  Book a Consultation
                </Button>
              </div>
            </div>

            {/* Related Posts */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-foreground mb-6">Related Articles</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {wellnessBlogPosts
                  .filter(p => p.id !== postId)
                  .slice(0, 2)
                  .map(relatedPost => (
                    <Link 
                      key={relatedPost.id}
                      to={`/wellness/blog/${relatedPost.id}`}
                      className="group p-6 bg-card rounded-xl border border-border hover:shadow-lg transition-shadow"
                    >
                      <span className="text-sm text-wellness font-semibold">{relatedPost.category}</span>
                      <h4 className="text-lg font-bold text-foreground mt-2 group-hover:text-wellness transition-colors">
                        {relatedPost.title}
                      </h4>
                      <p className="text-muted-foreground text-sm mt-2">{relatedPost.excerpt}</p>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </main>

        <TrueWellnessFooter />
        <LearnMoreModal open={learnMoreOpen} onOpenChange={setLearnMoreOpen} redirectToNuda={true} nudaLink="https://nudalabs.com/nudalabs-home/" />
        <NudaVitaeAssessmentModal 
          open={assessmentOpen} 
          onOpenChange={setAssessmentOpen} 
        />
      </div>
    </>
  );
};

export default WellnessBlogPost;
